/**
 * `/zadania/rodzaje` — the catalogue read, and the write behind each switch and each level.
 *
 * ── A press is drawn at once, and the catalogue is only ever written by the server's answer ───
 * A switch that waits for a round trip before it moves reads as broken. So the screen draws what
 * the server holds with the presses still on their way laid over it (`model/taskKinds`'
 * `withPendingWrites`, off the mutation cache), and the cache entry itself changes only when a PUT
 * is acknowledged — to the state and the level the acknowledgement names. Nothing is snapshotted
 * and nothing is rolled back: a refused press leaves the overlay and what the server holds is what
 * is left.
 *
 * ── One write at a time, in the order pressed ────────────────────────────────────────────────
 * Every step of the level is a PUT of its own (R25) and a trainer presses − three times in a
 * second. The writes share a mutation `scope`, so they reach the server one after another: three
 * concurrent PUTs could be applied in any order, and the level the server ended on was then not the
 * one the field showed.
 *
 * ── A refusal says so on the row, and the catalogue is read again ────────────────────────────
 * This app has no toasts and one is not being added for this. The refusal goes through the screen's
 * live region and onto the ROW, naming the level that is in force. A refusal a newer press on the
 * same control has already overtaken says nothing: the trainer's last word is still on its way, and
 * "it went back" would be false. Once the last write has settled after a refusal the catalogue is
 * read again — a PUT that timed out may have been applied.
 *
 * ── A read never lands over a newer answer ───────────────────────────────────────────────────
 * A read in flight when a press is made, or when an acknowledgement arrives, was asked before the
 * server held that write. It is cancelled, and made again when the writes have settled.
 *
 * ── Success invalidates the QUEUE's prefix, every zone of it ─────────────────────────────────
 * Muting a kind makes the server skip its detector, and a moved level changes which clients a low
 * reading fires for, so every cached queue answer is stale the moment the PUT lands — including the
 * Pulpit's, which reads the same cache entry. The key is a prefix with the zone at its tail
 * (`entities/client`), so one invalidation covers all of them.
 *
 * ── The preferences entry is `entities/client`'s ─────────────────────────────────────────────
 * Every chart and column that paints "low" reads the same entry (`useWellbeingLevels`), so an
 * acknowledged level repaints them without a request.
 */

import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import {
  attentionPreferencesQueryKey,
  attentionPreferencesQueryOptions,
  attentionQueueQueryKey,
  vitalsCatalogQueryOptions,
} from '~/entities/client';
import { putAttentionQueuePreferences } from '~/shared/api/generated/endpoints/attention-queue/attention-queue';
import type { TrainWithMeModulesAttentionDtosResponsesAttentionPreferencesResponse as AttentionPreferencesResponse } from '~/shared/api/generated/model';

import {
  kindWriteRequest,
  overtakes,
  taskKinds,
  taskKindsStatus,
  withAcknowledgement,
  withPendingWrites,
  type KindChange,
  type KindWrite,
  type TaskKindRow,
  type TaskKindsProjection,
  type TaskKindsStatus,
} from '../model/taskKinds';

/** What a refusal says, worded by the screen for the row as the SERVER holds it. */
type RefusalWords = (row: TaskKindRow) => string;

/** One press, with the words for its refusal. */
interface KindPress {
  readonly row: TaskKindRow;
  readonly write: KindWrite;
  readonly refusal: RefusalWords;
}

/** A refused write, for the sentence on its own row. */
export interface KindRefusal {
  readonly wireReason: string;
  readonly sentence: string;
}

export interface TaskKindsState {
  readonly status: TaskKindsStatus;
  readonly kinds: TaskKindsProjection;
  /** Set one kind's state. `muted` is what the trainer asked for, not what is there now. */
  readonly setMuted: (row: TaskKindRow, muted: boolean, refusal: RefusalWords) => void;
  /** Move one kind's level. `threshold` is one of the levels the catalogue offers. */
  readonly setLevel: (row: TaskKindRow, threshold: number, refusal: RefusalWords) => void;
  /** Return one kind's level to its default. */
  readonly resetLevel: (row: TaskKindRow, refusal: RefusalWords) => void;
  /** The write that was refused, so its own row can carry the sentence. `null` otherwise. */
  readonly refused: KindRefusal | null;
  readonly retry: () => void;
}

/** Every write of this screen: the key the pending ones are found by. */
const KIND_WRITE_KEY = ['attention-queue', 'preferences', 'write'] as const;
const PENDING_WRITES = { mutationKey: KIND_WRITE_KEY, status: 'pending' } as const;

/**
 * A mutation's variables as the press they are. The mutation cache types them `unknown`; every
 * mutation under `KIND_WRITE_KEY` is made by this hook, with a `KindPress`.
 */
function pressOf(variables: unknown): KindPress | undefined {
  return variables as KindPress | undefined;
}

export function useTaskKinds(announce: (message: string) => void): TaskKindsState {
  const queryClient = useQueryClient();
  const [refused, setRefused] = useState<KindRefusal | null>(null);
  /** The catalogue has to be read again once the writes have settled. */
  const rereadDue = useRef(false);

  const preferences = useQuery(attentionPreferencesQueryOptions());
  // Which levels the server accepts. The screen waits for it with the catalogue: a row that grew a
  // control when this landed would push every row under it.
  const vitals = useQuery(vitalsCatalogQueryOptions());

  /** The presses still on their way, oldest first. */
  const pending = useMutationState({
    filters: PENDING_WRITES,
    select: (mutation) => pressOf(mutation.state.variables)?.write,
  }).filter((write) => write !== undefined);

  /** Stop a read in flight from landing over a newer answer, and remember to make it again. */
  const holdRead = async () => {
    if (queryClient.isFetching({ queryKey: attentionPreferencesQueryKey }) > 0) rereadDue.current = true;
    await queryClient.cancelQueries({ queryKey: attentionPreferencesQueryKey });
  };

  const write = useMutation({
    mutationKey: KIND_WRITE_KEY,
    scope: { id: 'attention-preferences' },
    mutationFn: (press: KindPress) => putAttentionQueuePreferences(kindWriteRequest(press.write)),
    onMutate: async () => {
      setRefused(null);
      await holdRead();
    },
    onSuccess: async (acknowledgement, press) => {
      await holdRead();
      queryClient.setQueryData<AttentionPreferencesResponse>(attentionPreferencesQueryKey, (held) =>
        withAcknowledgement(held, press.write, acknowledgement),
      );
      // Every zone's queue is stale now — the server skips a muted kind's detector, and a moved
      // level changes who a low reading fires for.
      void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
    },
    onError: (_error, press) => {
      rereadDue.current = true;
      // This write is still counted among the pending ones here, so "a newer one" is a second.
      const waiting = queryClient
        .getMutationCache()
        .findAll(PENDING_WRITES)
        .flatMap((mutation) => pressOf(mutation.state.variables)?.write ?? []);
      if (waiting.filter((other) => overtakes(other, press.write)).length > 1) return;

      // The row as the server holds it — which is what the screen draws now that this press is gone.
      const held = taskKinds(
        queryClient.getQueryData(attentionPreferencesQueryKey),
        queryClient.getQueryData(vitalsCatalogQueryOptions().queryKey),
      )
        .groups.flatMap((group) => group.rows)
        .find((row) => row.wireReason === press.write.wireReason);
      const sentence = press.refusal(held ?? press.row);
      setRefused({ wireReason: press.write.wireReason, sentence });
      announce(sentence);
    },
    onSettled: () => {
      // This write is the last one still counted: nothing newer can be overwritten by the read.
      if (!rereadDue.current || queryClient.isMutating({ mutationKey: KIND_WRITE_KEY }) > 1) return;
      rereadDue.current = false;
      void queryClient.invalidateQueries({ queryKey: attentionPreferencesQueryKey });
    },
  });

  const kinds = taskKinds(withPendingWrites(preferences.data, pending), vitals.data);
  const unread = preferences.data === undefined;
  const send = (row: TaskKindRow, change: KindChange, refusal: RefusalWords) => {
    write.mutate({ row, write: { wireReason: row.wireReason, change }, refusal });
  };

  return {
    status: taskKindsStatus(
      {
        // A retry is a wait: an errored query keeps `isPending: false` while it refetches, so the
        // press drew nothing and the catalogue then appeared with no hand-over before it. The
        // vitals catalogue is waited for but never failed on: without it a level row draws no
        // control, and every switch still works.
        //
        // Failed with NOTHING to draw: the read made again after a refusal can fail too, and it
        // must not take the catalogue — and the refusal's own sentence — off the screen.
        pending:
          preferences.isPending || (unread && preferences.isError && preferences.isFetching) || vitals.isPending,
        failed: unread && preferences.isError,
      },
      kinds,
    ),
    kinds,
    setMuted: (row, muted, refusal) => {
      send(row, { kind: 'mute', muted }, refusal);
    },
    setLevel: (row, threshold, refusal) => {
      send(row, { kind: 'level', threshold }, refusal);
    },
    resetLevel: (row, refusal) => {
      if (row.level === null) return;
      send(row, { kind: 'resetLevel' }, refusal);
    },
    refused,
    retry: () => {
      void preferences.refetch();
    },
  };
}
